import { useEffect } from 'react';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import {
    contouringToolsParkedState,
    selectedToolState,
    Tools,
    writesContours,
} from '~/state/atoms/toolsAtoms';

/**
 * Where a parked tool is sent. Pan is the neutral tool, so parking leaves
 * the viewport navigable.
 */
const PARKED_TOOL: Tools = 'pan';

/**
 * Whether `tool` is one this hook parks: a tool whose drag writes to the
 * contours of the selected ROI. The hazard is a press that misses a resize
 * handle by a few pixels and lands on the image as a stroke instead.
 * `transform` paints nothing, but it drags the whole structure, which is the
 * same class of unintended, hard-to-notice edit. Viewing tools and the POI
 * move are not parked: they cannot change a contour, and parking them would
 * take working navigation away from a user who is trying to place the box.
 *
 * Exported so the toolbar buttons are disabled from the same rule that does
 * the parking: a tool that is parked but still reads as armed is the "UI
 * lies about its state" defect.
 */
export const isParkedContouringTool = (tool: Tools): boolean =>
    writesContours(tool);

/**
 * Whether a box is currently parking the ROI-editing tools. For the button
 * that must show itself unavailable while it lasts; the parking itself is
 * done by {@link useParkedContouringTool} alone.
 */
export function useContouringToolsParked(): boolean {
    return useRecoilValue(contouringToolsParkedState);
}

/**
 * Parks the ROI-editing tools while `active` is true: one that is selected,
 * or is selected while it lasts, gives way to Pan, and the toolbar shows
 * them as unavailable.
 *
 * While a draggable VOI box is on screen, a drag that starts slightly off
 * a handle must not paint. Scope is the WHOLE time the box is shown, not
 * just the handle drag — the stray drag this exists to stop is by
 * definition one that never reaches a handle.
 *
 * It parks through `selectedToolState`, never through `ToolManager`, so
 * the toolbar and the views agree on the tool in use.
 */
export function useParkedContouringTool(active: boolean): void {
    const [selectedTool, setSelectedTool] = useRecoilState(selectedToolState);
    const setParked = useSetRecoilState(contouringToolsParkedState);

    useEffect(() => {
        if (active && isParkedContouringTool(selectedTool)) {
            setSelectedTool(PARKED_TOOL);
        }
    }, [active, selectedTool, setSelectedTool]);

    // Publishes the parking so the toolbar can render the parked tools as
    // unavailable. Kept as its own effect, and cleared on unmount, so the
    // flag can never outlive the box that raised it — a toolbar left
    // permanently disabled would be the worse half of the same lie.
    useEffect(() => {
        setParked(active);
        return () => setParked(false);
    }, [active, setParked]);
}
